import { createReactDevMeasureCleanup } from './reactDevMeasures';

interface WegicPerfReportOptions {
  topN?: number;
  log?: boolean;
}

interface ResourceSnapshot {
  name: string;
  origin: string;
  initiatorType: string;
  transferSize: number;
  encodedBodySize: number;
  decodedBodySize: number;
  duration: number;
}

interface AggregateSnapshot {
  count: number;
  transferSize: number;
  encodedBodySize: number;
  decodedBodySize: number;
}

interface IframeSnapshot {
  index: number;
  src: string;
  origin: string;
  title: string;
  className: string;
  width: number;
  height: number;
  display: string;
  visibility: string;
  hidden: boolean;
}

interface ImageSnapshot {
  src: string;
  origin: string;
  naturalWidth: number;
  naturalHeight: number;
  complete: boolean;
  estimatedDecodedBytes: number;
}

interface CanvasSnapshot {
  index: number;
  width: number;
  height: number;
  estimatedBytes: number;
}

interface UserTimingSnapshot {
  measures: number;
  marks: number;
  /** Top measure names by count, useful for spotting runaway `performance.measure` callers. */
  topMeasureNames: Array<[string, number]>;
}

interface UaSpecificMemorySnapshot {
  bytes?: number;
  breakdown?: Array<{
    bytes: number;
    types?: string[];
    attribution?: Array<{
      scope?: string;
      url?: string;
      container?: {
        id?: string;
        src?: string;
      };
    }>;
  }>;
  error?: string;
}

interface WegicPerfRawReport {
  time: string;
  url: string;
  title: string;
  userAgent: string;
  memory: {
    jsHeapSizeLimit: number;
    totalJSHeapSize: number;
    usedJSHeapSize: number;
  } | null;
  uaSpecificMemory: UaSpecificMemorySnapshot | null;
  dom: {
    nodes: number;
    iframes: number;
    images: number;
    canvases: number;
    scripts: number;
    stylesheets: number;
  };
  iframes: IframeSnapshot[];
  images: ImageSnapshot[];
  canvases: CanvasSnapshot[];
  resources: ResourceSnapshot[];
  navigation: PerformanceNavigationTiming | null;
  paints: PerformanceEntry[];
  longTasks: PerformanceEntry[];
  userTiming: UserTimingSnapshot;
  eventCounts: Array<[string, number]>;
}

const DEFAULT_TOP_N = 15;
const reactDevMeasureCleanup = typeof performance !== 'undefined' && typeof PerformanceObserver !== 'undefined'
  ? createReactDevMeasureCleanup()
  : null;

import.meta.webpackHot?.dispose(() => {
  reactDevMeasureCleanup?.stop();
});

function formatBytes(value: number | undefined | null) {
  if (!Number.isFinite(value)) {
    return 'N/A';
  }

  const units = ['B', 'KB', 'MB', 'GB'];
  let size = Number(value);
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }

  return `${size.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}

function formatMs(value: number | undefined) {
  if (!Number.isFinite(value)) {
    return 'N/A';
  }

  return `${Math.round(Number(value))} ms`;
}

function escapeTableCell(value: string) {
  return value.replace(/\|/g, '\\|').replace(/\n/g, ' ');
}

function parseUrl(value: string) {
  try {
    return new URL(value, window.location.href);
  } catch {
    return null;
  }
}

function normalizeUrl(value: string) {
  const url = parseUrl(value);

  if (!url) {
    return String(value || '');
  }

  return `${url.origin}${url.pathname}`;
}

function getOrigin(value: string) {
  const url = parseUrl(value);
  return url?.origin || 'unknown';
}

function sum<T>(items: T[], selector: (item: T) => number) {
  return items.reduce((total, item) => total + (Number(selector(item)) || 0), 0);
}

function aggregateBy<T>(items: T[], getKey: (item: T) => string, getSizes: (item: T) => Omit<AggregateSnapshot, 'count'>) {
  const map = new Map<string, AggregateSnapshot>();

  for (const item of items) {
    const key = getKey(item) || 'unknown';
    const sizes = getSizes(item);
    const current = map.get(key) || {
      count: 0,
      transferSize: 0,
      encodedBodySize: 0,
      decodedBodySize: 0,
    };

    current.count += 1;
    current.transferSize += Number(sizes.transferSize) || 0;
    current.encodedBodySize += Number(sizes.encodedBodySize) || 0;
    current.decodedBodySize += Number(sizes.decodedBodySize) || 0;
    map.set(key, current);
  }

  return [...map.entries()].sort((a, b) => b[1].decodedBodySize - a[1].decodedBodySize);
}

async function getUaSpecificMemory(): Promise<UaSpecificMemorySnapshot | null> {
  const memoryApi = performance.measureUserAgentSpecificMemory;

  if (typeof memoryApi !== 'function') {
    return null;
  }

  try {
    return await memoryApi.call(performance);
  } catch (error) {
    return {
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

function getEventCounts() {
  const eventCounts = performance.eventCounts;

  if (!eventCounts) {
    return [];
  }

  return [...eventCounts.entries()];
}

function collectResources(): ResourceSnapshot[] {
  return performance.getEntriesByType('resource').map((entry) => {
    const resource = entry as PerformanceResourceTiming;

    return {
      name: normalizeUrl(resource.name),
      origin: getOrigin(resource.name),
      initiatorType: resource.initiatorType || 'unknown',
      transferSize: Number(resource.transferSize) || 0,
      encodedBodySize: Number(resource.encodedBodySize) || 0,
      decodedBodySize: Number(resource.decodedBodySize) || 0,
      duration: Number(resource.duration) || 0,
    };
  });
}

function collectIframes(): IframeSnapshot[] {
  return [...document.querySelectorAll('iframe')].map((iframe, index) => {
    const style = getComputedStyle(iframe);
    const src = iframe.src || '';

    return {
      index,
      src: normalizeUrl(src),
      origin: getOrigin(src),
      title: iframe.title || '',
      className: String(iframe.className || ''),
      width: iframe.clientWidth,
      height: iframe.clientHeight,
      display: style.display,
      visibility: style.visibility,
      hidden: iframe.hidden,
    };
  });
}

function collectImages(): ImageSnapshot[] {
  return [...document.images].map((image) => {
    const src = image.currentSrc || image.src || '';
    const naturalWidth = image.naturalWidth || 0;
    const naturalHeight = image.naturalHeight || 0;

    return {
      src: normalizeUrl(src),
      origin: getOrigin(src),
      naturalWidth,
      naturalHeight,
      complete: image.complete,
      estimatedDecodedBytes: naturalWidth * naturalHeight * 4,
    };
  });
}

function collectCanvases(): CanvasSnapshot[] {
  return [...document.querySelectorAll('canvas')].map((canvas, index) => ({
    index,
    width: canvas.width,
    height: canvas.height,
    estimatedBytes: canvas.width * canvas.height * 4,
  }));
}

function collectUserTiming(): UserTimingSnapshot {
  const measures = performance.getEntriesByType('measure');
  const marks = performance.getEntriesByType('mark');
  const nameCounts = new Map<string, number>();

  for (const measure of measures) {
    // React 19 dev writes User Timing entries prefixed with a zero-width space
    // (U+200B) for the DevTools "React" track; strip it so names are readable.
    const name = measure.name.replace(/\u200B/g, '') || '(empty)';
    nameCounts.set(name, (nameCounts.get(name) || 0) + 1);
  }

  return {
    measures: measures.length,
    marks: marks.length,
    topMeasureNames: [...nameCounts.entries()].sort((a, b) => b[1] - a[1]),
  };
}

export async function getWegicPerfRawReport(): Promise<WegicPerfRawReport> {
  const memory = performance.memory
    ? {
        jsHeapSizeLimit: performance.memory.jsHeapSizeLimit,
        totalJSHeapSize: performance.memory.totalJSHeapSize,
        usedJSHeapSize: performance.memory.usedJSHeapSize,
      }
    : null;
  const iframes = collectIframes();
  const images = collectImages();
  const canvases = collectCanvases();

  return {
    time: new Date().toISOString(),
    url: window.location.href,
    title: document.title,
    userAgent: navigator.userAgent,
    memory,
    uaSpecificMemory: await getUaSpecificMemory(),
    dom: {
      nodes: document.getElementsByTagName('*').length,
      iframes: iframes.length,
      images: images.length,
      canvases: canvases.length,
      scripts: document.scripts.length,
      stylesheets: document.styleSheets.length,
    },
    iframes,
    images,
    canvases,
    resources: collectResources(),
    navigation: (performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined) ?? null,
    paints: performance.getEntriesByType('paint'),
    longTasks: performance.getEntriesByType('longtask'),
    userTiming: collectUserTiming(),
    eventCounts: getEventCounts(),
  };
}

export async function getWegicPerfMarkdownReport(options: WegicPerfReportOptions = {}) {
  const topN = options.topN ?? DEFAULT_TOP_N;
  const raw = await getWegicPerfRawReport();
  const resourceGroups = aggregateBy(
    raw.resources,
    resource => resource.initiatorType,
    resource => resource,
  );
  const originGroups = aggregateBy(
    raw.resources,
    resource => resource.origin,
    resource => resource,
  );
  const topResources = [...raw.resources]
    .sort((a, b) => b.decodedBodySize - a.decodedBodySize)
    .slice(0, topN);
  const topImages = [...raw.images]
    .sort((a, b) => b.estimatedDecodedBytes - a.estimatedDecodedBytes)
    .slice(0, topN);
  const topLongTasks = [...raw.longTasks]
    .sort((a, b) => b.duration - a.duration)
    .slice(0, topN);
  const lines: string[] = [];

  lines.push('# Wegic Performance Report');
  lines.push('');
  lines.push(`- Time: ${raw.time}`);
  lines.push(`- URL: ${raw.url}`);
  lines.push(`- Title: ${raw.title}`);
  lines.push('');

  lines.push('## JS Heap');
  if (raw.memory) {
    lines.push(`- usedJSHeapSize: ${formatBytes(raw.memory.usedJSHeapSize)}`);
    lines.push(`- totalJSHeapSize: ${formatBytes(raw.memory.totalJSHeapSize)}`);
    lines.push(`- jsHeapSizeLimit: ${formatBytes(raw.memory.jsHeapSizeLimit)}`);
  } else {
    lines.push('- performance.memory unavailable');
  }
  lines.push('');

  lines.push('## UA Specific Memory');
  if (!raw.uaSpecificMemory) {
    lines.push('- measureUserAgentSpecificMemory unavailable');
  } else if (raw.uaSpecificMemory.error) {
    lines.push(`- Error: ${raw.uaSpecificMemory.error}`);
  } else {
    lines.push(`- bytes: ${formatBytes(raw.uaSpecificMemory.bytes)}`);
    raw.uaSpecificMemory.breakdown?.slice(0, topN).forEach((item) => {
      const types = item.types?.join(', ') || 'unknown';
      const attribution = item.attribution
        ?.map(part => part.url || part.container?.src || part.scope)
        .filter(Boolean)
        .map(String)
        .map(normalizeUrl)
        .join(', ');
      lines.push(`- ${types}: ${formatBytes(item.bytes)}${attribution ? ` (${attribution})` : ''}`);
    });
  }
  lines.push('');

  lines.push('## DOM');
  lines.push(`- DOM nodes: ${raw.dom.nodes}`);
  lines.push(`- Iframes: ${raw.dom.iframes}`);
  lines.push(`- Images: ${raw.dom.images}`);
  lines.push(`- Canvas: ${raw.dom.canvases}`);
  lines.push(`- Scripts: ${raw.dom.scripts}`);
  lines.push(`- Stylesheets: ${raw.dom.stylesheets}`);
  lines.push('');

  lines.push('## Iframes');
  if (raw.iframes.length === 0) {
    lines.push('- No iframe found');
  } else {
    raw.iframes.forEach((iframe) => {
      lines.push(
        `- #${iframe.index} ${iframe.width}x${iframe.height} display=${iframe.display} visibility=${iframe.visibility} hidden=${iframe.hidden} title="${iframe.title}" src=${iframe.src}`,
      );
    });
  }
  lines.push('');

  lines.push('## Resource Summary By Type');
  lines.push('| Type | Count | Transfer | Encoded | Decoded |');
  lines.push('|---|---:|---:|---:|---:|');
  resourceGroups.forEach(([type, stat]) => {
    lines.push(`| ${escapeTableCell(type)} | ${stat.count} | ${formatBytes(stat.transferSize)} | ${formatBytes(stat.encodedBodySize)} | ${formatBytes(stat.decodedBodySize)} |`);
  });
  lines.push(`| TOTAL | ${raw.resources.length} | ${formatBytes(sum(raw.resources, item => item.transferSize))} | ${formatBytes(sum(raw.resources, item => item.encodedBodySize))} | ${formatBytes(sum(raw.resources, item => item.decodedBodySize))} |`);
  lines.push('');

  lines.push('## Resource Summary By Origin');
  lines.push('| Origin | Count | Transfer | Encoded | Decoded |');
  lines.push('|---|---:|---:|---:|---:|');
  originGroups.slice(0, topN).forEach(([origin, stat]) => {
    lines.push(`| ${escapeTableCell(origin)} | ${stat.count} | ${formatBytes(stat.transferSize)} | ${formatBytes(stat.encodedBodySize)} | ${formatBytes(stat.decodedBodySize)} |`);
  });
  lines.push('');

  lines.push('## Top Resources By Decoded Size');
  if (topResources.length === 0) {
    lines.push('- No resource timing entries');
  } else {
    topResources.forEach((resource, index) => {
      lines.push(`${index + 1}. ${formatBytes(resource.decodedBodySize)} [${resource.initiatorType}] ${resource.name}`);
    });
  }
  lines.push('');

  lines.push('## Top Images By Estimated Decoded Size');
  if (topImages.length === 0) {
    lines.push('- No image found');
  } else {
    topImages.forEach((image, index) => {
      lines.push(`${index + 1}. ${formatBytes(image.estimatedDecodedBytes)} ${image.naturalWidth}x${image.naturalHeight} complete=${image.complete} ${image.src}`);
    });
  }
  lines.push('');

  lines.push('## Canvas Estimated Memory');
  if (raw.canvases.length === 0) {
    lines.push('- No canvas found');
  } else {
    raw.canvases.forEach((canvas) => {
      lines.push(`- #${canvas.index} ${canvas.width}x${canvas.height}: ${formatBytes(canvas.estimatedBytes)}`);
    });
  }
  lines.push('');

  lines.push('## Timing');
  if (raw.navigation) {
    lines.push(`- DOMContentLoaded: ${formatMs(raw.navigation.domContentLoadedEventEnd)}`);
    lines.push(`- Load: ${formatMs(raw.navigation.loadEventEnd)}`);
    lines.push(`- Navigation transfer: ${formatBytes(raw.navigation.transferSize)}`);
    lines.push(`- Navigation decoded body: ${formatBytes(raw.navigation.decodedBodySize)}`);
  } else {
    lines.push('- Navigation timing unavailable');
  }
  raw.paints.forEach((paint) => {
    lines.push(`- ${paint.name}: ${formatMs(paint.startTime)}`);
  });
  lines.push(`- Long tasks recorded: ${raw.longTasks.length}`);
  lines.push(`- Long tasks total duration: ${formatMs(sum(raw.longTasks, task => task.duration))}`);
  topLongTasks.forEach((task, index) => {
    lines.push(`  ${index + 1}. ${formatMs(task.duration)} at ${formatMs(task.startTime)}`);
  });
  lines.push('');

  lines.push('## Event Counts');
  if (raw.eventCounts.length === 0) {
    lines.push('- performance.eventCounts unavailable or empty');
  } else {
    raw.eventCounts.forEach(([name, count]) => {
      lines.push(`- ${name}: ${count}`);
    });
  }
  lines.push('');

  lines.push('## User Timing (performance.measure / mark)');
  lines.push(`- Measures buffered: ${raw.userTiming.measures}`);
  lines.push(`- Marks buffered: ${raw.userTiming.marks}`);
  if (raw.userTiming.measures > 10000) {
    lines.push(`- ⚠️ ${raw.userTiming.measures} buffered measures retain PerformanceMeasure objects on the timeline. Wegic automatically clears React 19 dev measures after they reach the cleanup threshold; call window.__wegicPerfClear() only when you need to reset the entire diagnostic timeline.`);
  }
  raw.userTiming.topMeasureNames.slice(0, topN).forEach(([name, count]) => {
    lines.push(`- ${count} × ${name}`);
  });
  lines.push('');

  lines.push('## Notes');
  lines.push('- This report reads the current document. Cross-origin iframe internals are not directly inspectable from the parent page.');
  lines.push('- Chrome tab memory may include renderer, subframes, decoded images, raster/GPU caches, DevTools, and heap snapshots; it is not equivalent to JS heap.');
  lines.push('- Avoid expanding DOM objects in Console while measuring memory, because DevTools can retain inspected objects.');

  const markdown = lines.join('\n');

  if (options.log !== false) {
    // eslint-disable-next-line no-console
    console.log(markdown);
  }

  return markdown;
}

/**
 * Clears buffered marks/measures/resource timing so a heap snapshot taken
 * afterwards is not polluted by accumulated PerformanceMeasure objects
 * (notably React 19's dev-only User Timing entries).
 */
export function clearPerfTimeline() {
  const before = {
    measures: performance.getEntriesByType('measure').length,
    marks: performance.getEntriesByType('mark').length,
    resources: performance.getEntriesByType('resource').length,
  };

  performance.clearMeasures();
  performance.clearMarks();
  performance.clearResourceTimings();
  reactDevMeasureCleanup?.syncCount();

  // eslint-disable-next-line no-console
  console.info('[PerformanceDebug] cleared timeline', before);

  return before;
}

export function installPerformanceDebugReport() {
  window.__wegicPerfReport = getWegicPerfMarkdownReport;
  window.__wegicPerfReportRaw = getWegicPerfRawReport;
  window.__wegicPerfClear = clearPerfTimeline;
  reactDevMeasureCleanup?.start();

  // eslint-disable-next-line no-console
  console.info('[PerformanceDebug] injected: await window.__wegicPerfReport() / window.__wegicPerfClear()');
}
